import React, { useState } from "react";
import { ArrowLeft, Download, Printer, FileSpreadsheet, DollarSign, TrendingUp, CheckCircle, Edit2, Check, X, Bot, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function AssessmentPreview({
  assessmentResults,
  caseData,
  onNavigateBack,
  onExportPDF,
  currentUser,
  lineDecisions: controlledLineDecisions,
  onLineDecisionsChange,
}) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedPrices, setEditedPrices] = useState({});
  const [tempPrice, setTempPrice] = useState('');
  // Per-line user decision for the ACTIONS column: 'accept' | 'reject' | 'modified'
  // Supports both controlled (parent-owned) and uncontrolled usage.
  const [uncontrolledLineDecisions, setUncontrolledLineDecisions] = useState({});
  const lineDecisions = controlledLineDecisions ?? uncontrolledLineDecisions;
  const setLineDecisions = (updater) => {
    if (onLineDecisionsChange) {
      onLineDecisionsChange((prev) =>
        typeof updater === "function" ? updater(prev ?? {}) : updater
      );
    } else {
      setUncontrolledLineDecisions(updater);
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getDamageText = (code) => {
    const damageTypes = {
      1: "Total loss",
      2: "Clean on site",
      3: "Clean and inspect on site", 
      4: "Clean and handle on site",
      5: "Clean, inspect, and handle on site",
      6: "Clean, inspect, repair, and handle on site",
      7: "Remove, transport off site, clean, inspect, and reinstall",
      8: "Remove, transport off site, clean, inspect, repair, and reinstall",
      9: "No damage",
      10: "Belongs to a third party, insured not liable"
    };
    return damageTypes[code] || `Damage Code ${code}`;
  };

  const calculateDepreciationPercent = (marketPrice, depreciationAmount) => {
    if (marketPrice === 0) return 0;
    return Math.round((depreciationAmount / marketPrice) * 100);
  };

  const getPayoutPercentage = (damageCode) => {
    const percentages = { 
      1: 100, // Total loss
      2: 9,   // Clean on site  
      3: 11,  // Clean and inspect on site
      4: 12,  // Clean and handle on site
      5: 14,  // Clean, inspect, and handle on site
      6: 16,  // Clean, inspect, repair, and handle on site
      7: 18,  // Remove, transport off site, clean, inspect, and reinstall
      8: 20,  // Remove, transport off site, clean, inspect, repair, and reinstall
      9: 0,   // No damage
      10: 0   // Belongs to a third party, insured not liable
    };
    return percentages[damageCode] || 0;
  };

  // Compute match confidence purely from the similarity between the user's
  // original request description and the best-match description Blexbot
  // resolved. Uses a bidirectional token-overlap ratio (ignoring short
  // stop-words and punctuation) so partial matches scale smoothly.
  const calculateMatchConfidence = (original, matched) => {
    const originalDesc = (original?.description || "").toString();
    const matchedDesc = (matched?.description || "").toString();
    if (!originalDesc.trim() || !matchedDesc.trim()) return 0;

    const tokenize = (s) =>
      s
        .toLowerCase()
        .replace(/[^a-z0-9\s"']/g, " ")
        .split(/\s+/)
        .filter((w) => w.length >= 2);

    const origTokens = tokenize(originalDesc);
    const matchTokens = tokenize(matchedDesc);
    if (origTokens.length === 0 || matchTokens.length === 0) return 0;

    const origSet = new Set(origTokens);
    const matchSet = new Set(matchTokens);

    // How many of the user's words appear in the matched title (substring OK).
    let hits = 0;
    for (const w of origSet) {
      const found = [...matchSet].some(
        (mw) => mw === w || mw.includes(w) || w.includes(mw)
      );
      if (found) hits += 1;
    }

    const ratio = hits / origSet.size;
    return Math.max(0, Math.min(100, Math.round(ratio * 100)));
  };

  // Resolve the confidence to display for a result. Always derived from the
  // description-vs-description similarity between USER REQUEST and BEST MATCH
  // so the % the user sees reflects what the two columns show.
  const resolveConfidence = (result) => {
    if (!result?.matched_product) return 0;
    return calculateMatchConfidence(result.original_claim, {
      description: bestMatchTitle(result),
    });
  };

  // A result is "AI-researched" when the backend priced it without matching a
  // product from the internal DB (matched_product is null but a price is set).
  const isAiResearched = (result) =>
    !result?.matched_product && (result?.market_price || 0) > 0;

  // Best Match shows only what the source link / Blexbot resolved — never the customer's text.
  const normText = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const notEcho = (value, customer) => (normText(value) && normText(value) !== normText(customer) ? value : "");
  const bestMatchTitle = (result) =>
    notEcho(result?.matched_product?.description, result?.original_claim?.description);
  const bestMatchMakerModel = (result) => {
    const mm = [result?.matched_product?.maker, result?.matched_product?.model].filter(Boolean).join(" ");
    const customer = [result?.original_claim?.maker, result?.original_claim?.model].filter(Boolean).join(" ");
    return notEcho(mm, customer);
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return "text-green-700 bg-green-50 border-green-200";
    if (confidence >= 60) return "text-yellow-700 bg-yellow-50 border-yellow-200";
    if (confidence >= 40) return "text-orange-700 bg-orange-50 border-orange-200";
    return "text-red-700 bg-red-50 border-red-200";
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 80) return "High";
    if (confidence >= 60) return "Medium";
    if (confidence >= 40) return "Low";
    return "Very Low";
  };

  const startEditing = (index, currentPrice) => {
    setEditingIndex(index);
    setTempPrice(currentPrice.toString());
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setTempPrice('');
  };

  const saveEditedPrice = (index) => {
    const newPrice = parseFloat(tempPrice);
    if (!isNaN(newPrice) && newPrice >= 0) {
      setEditedPrices({
        ...editedPrices,
        [index]: newPrice
      });
    }
    setEditingIndex(null);
    setTempPrice('');
  };

  const getPrice = (index, originalPrice) => {
    return editedPrices[index] !== undefined ? editedPrices[index] : originalPrice;
  };

  // Sum of market prices for all rows EXCEPT those the user marked "reject".
  // Shared across the Dashboard tile, the printed PDF, and the Total row.
  const computeTotalExcludingRejected = () => {
    const rows = assessmentResults?.results || [];
    return rows.reduce((sum, r, index) => {
      if (lineDecisions[index] === "reject") return sum;
      return sum + getPrice(index, r.market_price);
    }, 0);
  };

  const isPriceEdited = (index) => {
    return editedPrices[index] !== undefined;
  };

  const handlePrint = async () => {
    try {
      console.log('Generating PDF...');
      
      const doc = new jsPDF();
      
      // Add PriceValuator title instead of logo
      doc.setFontSize(20);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(20, 184, 166); // Teal color
      doc.text('PriceValuator', 15, 18);
      
      // Add Case Number and User ID (right side)
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Case ${caseData?.case_number || 'N/A'}`, 200, 15, { align: 'right' });
      if (caseData?.blexbot_request_number) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`ID # ${caseData.blexbot_request_number}`, 200, 20, { align: 'right' });
      }
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`User ID: ${currentUser?.username || 'N/A'}`, 200, 22, { align: 'right' });
      
      // Add line separator
      doc.setDrawColor(20, 184, 166); // Teal color
      doc.setLineWidth(1);
      doc.line(15, 30, 195, 30);
      
      // Add Indicators
      let yPos = 40;
      
      // Total Valuation Box
      doc.setFillColor(209, 250, 229); // Light green
      doc.rect(15, yPos, 85, 25, 'F');
      doc.setDrawColor(16, 185, 129); // Green border
      doc.setLineWidth(0.5);
      doc.rect(15, yPos, 85, 25, 'S');
      doc.setFontSize(10);
      doc.setTextColor(6, 95, 70); // Dark green
      doc.text('Total Valuation', 20, yPos + 8);
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      const totalVal = computeTotalExcludingRejected();
      doc.text(formatCurrency(totalVal), 20, yPos + 20);
      
      // Total Products Valuated Box
      doc.setFillColor(233, 213, 255); // Light purple
      doc.rect(110, yPos, 85, 25, 'F');
      doc.setDrawColor(168, 85, 247); // Purple border
      doc.rect(110, yPos, 85, 25, 'S');
      doc.setTextColor(107, 33, 168); // Dark purple
      doc.setFontSize(10);
      doc.text('Total Products Valuated', 115, yPos + 8);
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      const totalProducts = assessmentResults?.results?.reduce((sum, r) => sum + r.original_claim.qty, 0) || 0;
      doc.text(totalProducts.toString(), 115, yPos + 20);
      
      // Assessment Results Title
      yPos = 75;
      doc.setFontSize(14);
      doc.setTextColor(79, 70, 229); // Indigo
      doc.setFont(undefined, 'bold');
      doc.text('Assessment Results', 15, yPos);
      
      // Prepare table data
      const tableData = assessmentResults?.results?.map((result, index) => {
        return [
          result.original_claim.qty.toString(),
          [bestMatchTitle(result), bestMatchMakerModel(result)].filter(Boolean).join("\n"),
          formatCurrency(getPrice(index, result.market_price))
        ];
      }) || [];
      
      // Add table
      autoTable(doc, {
        startY: yPos + 5,
        head: [['QTY', 'BEST MATCH', 'PRICE']],
        body: tableData,
        foot: [['', 'TOTAL VALUATION', formatCurrency(totalVal)]],
        theme: 'grid',
        headStyles: {
          fillColor: [243, 244, 246],
          textColor: [55, 65, 81],
          fontStyle: 'bold',
          fontSize: 10
        },
        footStyles: {
          fillColor: [243, 244, 246],
          textColor: [4, 120, 87],
          fontStyle: 'bold',
          fontSize: 11
        },
        styles: {
          font: 'helvetica',
          fontSize: 9,
          cellPadding: 5
        },
        columnStyles: {
          0: { cellWidth: 20, halign: 'center' },
          1: { cellWidth: 120 },
          2: { cellWidth: 40, halign: 'right' }
        },
        // Colour every row's Market Price cell green (or blue when the price
        // has been inline-edited by the appraiser). Per-row colouring is done
        // here rather than via columnStyles.textColor because that only
        // supports a single flat [r,g,b] value.
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index === 2) {
            data.cell.styles.textColor = isPriceEdited(data.row.index)
              ? [37, 99, 235]
              : [5, 150, 105];
          }
        }
      });
      
      // Save the PDF
      const filename = `Assessment_${caseData?.case_number || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);
      
      console.log('PDF generated successfully:', filename);
      alert(`PDF generated successfully!\nFile: ${filename}\nCheck your Downloads folder.`);
      
    } catch (error) {
      console.error('PDF generation error:', error);
      alert(`Error generating PDF: ${error.message}`);
    }
  };

  const renderLineImage = (result, index, p = "") => {
    const aiResearched = isAiResearched(result);
    const productImgUrl =
      result.image_url ||
      result.matched_product?.image_url ||
      result.matched_product?.image_base64;
    const hasMatch = !!result.matched_product;
    const lineStatus = String(result.status || "").toLowerCase();
    // "Working…" rows (no_match) also show the pulsing
    // bot icon so the user sees we're still trying.
    const isStillSearching =
      lineStatus === "pending" ||
      lineStatus === "researching" ||
      lineStatus === "no_match";

    // While Blexbot is still searching this line, show
    // the pulsing bot icon so the user sees progress —
    // even if the row ultimately won't resolve.
    if (isStillSearching) {
      return (
        <div
          data-testid={`${p}ai-image-${index}`}
          className="w-16 h-16 rounded border border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 flex flex-col items-center justify-center mx-auto"
          aria-label="Blexbot is still sourcing this product"
        >
          <Bot className="w-6 h-6 text-purple-600 animate-pulse" aria-hidden="true" />
          <span className="text-[9px] text-purple-700 font-semibold mt-0.5">
            searching…
          </span>
        </div>
      );
    }

    // Quoted row with a resolved image → render it.
    if (productImgUrl) {
      return (
        <img
          src={productImgUrl}
          alt={bestMatchTitle(result) || "Product image"}
          data-testid={`${p}preview-product-image-${index}`}
          onError={(e) => {
            // If the URL 404s, fall back to a bot placeholder.
            e.currentTarget.onerror = null;
            e.currentTarget.replaceWith(
              Object.assign(document.createElement("div"), {
                className:
                  "w-16 h-16 rounded border border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 flex flex-col items-center justify-center mx-auto",
                innerHTML:
                  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><line x1="8" y1="16" x2="8" y2="16"/><line x1="16" y1="16" x2="16" y2="16"/></svg>',
              })
            );
          }}
          className="w-16 h-16 object-contain rounded border border-gray-300 mx-auto bg-white product-image"
        />
      );
    }

    // AI-researched placeholder (legacy).
    if (hasMatch || aiResearched) {
      return (
        <div
          data-testid={`${p}ai-image-${index}`}
          className="w-16 h-16 rounded border border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 flex flex-col items-center justify-center mx-auto"
          aria-label="AI-provided image placeholder"
        >
          <Bot className="w-6 h-6 text-purple-600" aria-hidden="true" />
          <span className="text-[9px] text-purple-700 font-semibold mt-0.5">
            by Bot
          </span>
        </div>
      );
    }

    return (
      <div className="w-16 h-16 border border-dashed border-gray-300 rounded flex items-center justify-center mx-auto bg-gray-50">
        <span className="text-xs text-gray-400">No image</span>
      </div>
    );
  };

  const renderLineStatus = (result, index, p = "") => {
    const raw = String(result.status || (result.market_price > 0 ? "quoted" : "researching")).toLowerCase();
    const label =
      raw === "quoted" ? "Complete"
      : raw === "pending" ? "Pending"
      : raw === "no_match" ? "Working…"
      : "Researching";
    const cls =
      raw === "quoted"
        ? "bg-green-100 text-green-800 border-green-300"
        : raw === "pending"
        ? "bg-gray-100 text-gray-700 border-gray-300"
        : raw === "no_match"
        ? "bg-amber-100 text-amber-800 border-amber-300"
        : "bg-amber-100 text-amber-800 border-amber-300";
    // Pulse the dot for both researching AND no_match
    // (we're still trying — Blexbot may surface a
    // price when the user auto-refreshes).
    const pulsing = raw === "researching" || raw === "no_match";
    return (
      <span
        data-testid={`${p}line-status-${index}`}
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold ${cls}`}
      >
        <span
          className={`w-1.5 h-1.5 rounded-full ${
            raw === "quoted" ? "bg-green-500"
            : raw === "pending" ? "bg-gray-500"
            : raw === "no_match" ? "bg-amber-500"
            : "bg-amber-500"
          } ${pulsing ? "animate-pulse" : ""}`}
        ></span>
        {label}
      </span>
    );
  };

  const renderLinePrice = (result, index) => (
    editingIndex === index ? (
      <div className="flex items-center justify-end space-x-2">
        <Input
          type="number"
          step="0.01"
          value={tempPrice}
          onChange={(e) => setTempPrice(e.target.value)}
          className="w-28 h-8 text-right"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveEditedPrice(index);
            if (e.key === 'Escape') cancelEditing();
          }}
        />
        <Button
          size="sm"
          variant="ghost"
          onClick={() => saveEditedPrice(index)}
          className="h-8 w-8 p-0 text-green-600 hover:bg-green-50"
        >
          <Check className="w-4 h-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={cancelEditing}
          className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    ) : (
      <span className={`font-medium ${isPriceEdited(index) ? 'text-blue-700' : 'text-green-700'}`}>
        {formatCurrency(getPrice(index, result.market_price))}
      </span>
    )
  );

  const renderLineActions = (result, index, p = "") => (
    editingIndex !== index && (
      <div className="inline-flex items-center gap-1" data-testid={`${p}line-actions-${index}`}>
        {/* Accept */}
        <button
          type="button"
          data-testid={`${p}action-accept-${index}`}
          aria-label="Accept this match"
          title="Accept"
          onClick={() =>
            setLineDecisions((prev) => ({
              ...prev,
              [index]: prev[index] === "accept" ? undefined : "accept",
            }))
          }
          className={`h-11 w-11 md:h-8 md:w-8 rounded-full inline-flex items-center justify-center border transition-colors ${
            lineDecisions[index] === "accept"
              ? "bg-green-600 text-white border-green-700 shadow"
              : "text-green-700 bg-green-50 border-green-200 hover:bg-green-100"
          }`}
        >
          <Check className="w-4 h-4" />
        </button>
        {/* Not Accept */}
        <button
          type="button"
          data-testid={`${p}action-reject-${index}`}
          aria-label="Reject this match"
          title="Not Accept"
          onClick={() =>
            setLineDecisions((prev) => ({
              ...prev,
              [index]: prev[index] === "reject" ? undefined : "reject",
            }))
          }
          className={`h-11 w-11 md:h-8 md:w-8 rounded-full inline-flex items-center justify-center border transition-colors ${
            lineDecisions[index] === "reject"
              ? "bg-red-600 text-white border-red-700 shadow"
              : "text-red-700 bg-red-50 border-red-200 hover:bg-red-100"
          }`}
        >
          <X className="w-4 h-4" />
        </button>
        {/* Modified — opens price editing */}
        <button
          type="button"
          data-testid={`${p}action-modify-${index}`}
          aria-label="Mark as modified and edit price"
          title="Modified"
          onClick={() => {
            setLineDecisions((prev) => ({ ...prev, [index]: "modified" }));
            startEditing(index, getPrice(index, result.market_price));
          }}
          className={`h-11 w-11 md:h-8 md:w-8 rounded-full inline-flex items-center justify-center border transition-colors ${
            lineDecisions[index] === "modified"
              ? "bg-indigo-600 text-white border-indigo-700 shadow"
              : "text-indigo-700 bg-indigo-50 border-indigo-200 hover:bg-indigo-100"
          }`}
        >
          <Edit2 className="w-4 h-4" />
        </button>
      </div>
    )
  );

  const renderMobileCard = (result, index) => {
    const title = bestMatchTitle(result);
    const makerModel = bestMatchMakerModel(result);
    const confidence = resolveConfidence(result);
    const decision = lineDecisions[index];
    const claim = result.original_claim;
    const border =
      decision === "reject" ? "border-red-200 opacity-70"
      : decision === "accept" ? "border-green-300"
      : "border-gray-200";
    return (
      <div key={index} data-testid={`mobile-line-card-${index}`} className={`rounded-2xl border bg-white shadow-sm p-4 ${border}`}>
        <div className="flex gap-3">
          <div className="shrink-0">{renderLineImage(result, index, "mobile-")}</div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-gray-900 leading-snug break-words" data-testid={`mobile-user-request-${index}`}>
                {claim.description}
              </p>
              <span className="shrink-0 text-xs font-semibold text-gray-700 bg-gray-100 rounded-full px-2 py-0.5" data-testid={`mobile-qty-${index}`}>
                Qty {claim.qty}
              </span>
            </div>
            {(claim.maker || claim.model) && (
              <p className="text-xs text-gray-500 mt-1">{[claim.maker, claim.model].filter(Boolean).join(" ")}</p>
            )}
          </div>
        </div>
        <div className="mt-3 rounded-xl bg-gray-50 border border-gray-100 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Best match</p>
          <p className="text-sm font-medium text-gray-900 break-words mt-0.5" data-testid={`mobile-best-match-${index}`}>
            {title || "—"}
          </p>
          {makerModel && <p className="text-xs text-gray-500 mt-0.5">{makerModel}</p>}
          {result.source_url && (
            <a
              href={result.source_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`mobile-source-link-${index}`}
              className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-cyan-700 underline"
            >
              <ExternalLink className="w-3 h-3" aria-hidden="true" />
              View source
            </a>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          {renderLineStatus(result, index, "mobile-")}
          <span
            data-testid={`mobile-match-confidence-${index}`}
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-semibold ${title ? getConfidenceColor(confidence) : "text-gray-500 bg-gray-50 border-gray-200"}`}
          >
            {title ? `${confidence}% ${getConfidenceLabel(confidence)}` : "Confidence —"}
          </span>
        </div>
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between gap-3">
          <div className="text-base">{renderLinePrice(result, index)}</div>
          {renderLineActions(result, index, "mobile-")}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Image Hover Effect Styles */}
      <style>{`
        .product-image {
          transition: transform 0.3s ease, z-index 0s 0s;
          position: relative;
          cursor: pointer;
        }
        .product-image:hover {
          transform: scale(1.5);
          z-index: 50;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
        }
      `}</style>
      
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-50 via-white to-purple-50 border-b border-indigo-100">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                onClick={onNavigateBack}
                className="text-indigo-600 hover:bg-indigo-50 rounded-full px-4 py-2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Assessment
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-indigo-900">Assessment Preview</h1>
                <p className="text-indigo-600">
                  Case {caseData?.case_number}
                </p>
                {caseData?.blexbot_request_number && (
                  <div
                    data-testid="preview-header-radicado"
                    className="mt-1 inline-flex items-center text-xs font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 px-2 py-0.5 rounded-full"
                  >
                    ID # {caseData.blexbot_request_number}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row w-full md:w-auto gap-3" data-testid="preview-header-actions">
              {/* Accept-all CTA: fills every row's decision at once so the user
                  can one-click sign off when they're happy with the quote. */}
              {(() => {
                const rows = assessmentResults?.results || [];
                if (rows.length === 0) return null;
                const acceptedCount = rows.reduce(
                  (n, _r, i) => n + (lineDecisions[i] === "accept" ? 1 : 0),
                  0
                );
                const allAccepted = acceptedCount === rows.length;
                return (
                  <Button
                    onClick={() => {
                      const next = {};
                      rows.forEach((_r, i) => { next[i] = "accept"; });
                      setLineDecisions(next);
                    }}
                    disabled={allAccepted}
                    data-testid="accept-all-btn"
                    className={`w-full md:w-auto rounded-full px-5 py-3 shadow-md ${
                      allAccepted
                        ? "bg-green-600 text-white cursor-default"
                        : "bg-emerald-600 hover:bg-emerald-700 text-white"
                    }`}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {allAccepted ? "All lines accepted" : `Accept all (${acceptedCount}/${rows.length})`}
                  </Button>
                );
              })()}
              <Button 
                onClick={(e) => {
                  console.log('Button clicked - handler executing');
                  handlePrint(e);
                }}
                type="button"
                data-testid="print-pdf-btn"
                className="w-full md:w-auto bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-full px-6 py-3 shadow-lg hover:shadow-xl transition-all"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print PDF
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Spreadsheet-style Assessment */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Card className="rounded-2xl border-0 shadow-lg bg-white">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-t-2xl">
            <CardTitle className="flex items-center text-indigo-900">
              <div className="bg-indigo-100 p-2 rounded-full mr-3">
                <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
              </div>
              Assessment Results
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            
            {/* Dashboard Indicators */}
            <div className="p-6 grid md:grid-cols-2 gap-4">
              <div className="rounded-xl border-0 shadow-sm bg-gradient-to-br from-green-50 to-emerald-50 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-700">Total Valuation</p>
                    <p className="text-xl font-bold text-green-900">
                      {formatCurrency(assessmentResults?.total_payout || 0)}
                    </p>
                  </div>
                  <div className="bg-green-100 p-2 rounded-full">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  </div>
                </div>
              </div>

              <div className="rounded-xl border-0 shadow-sm bg-gradient-to-br from-purple-50 to-pink-50 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-700">Total Products Valuated</p>
                    <p className="text-xl font-bold text-purple-900">
                      {assessmentResults?.results?.reduce((sum, r) => sum + r.original_claim.qty, 0) || 0}
                    </p>
                  </div>
                  <div className="bg-purple-100 p-2 rounded-full">
                    <FileSpreadsheet className="h-4 w-4 text-purple-600" />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Simplified Assessment Results Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-xs font-semibold text-gray-700 text-center"></th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-700 text-center">QTY</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-700 text-left">USER REQUEST</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-700 text-left">BEST MATCH</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-700 text-center">MATCH CONFIDENCE</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-700 text-center">STATUS</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-700 text-right">PRICE</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-700 text-center">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {assessmentResults?.results?.map((result, index) => {
                    const matchConfidence = resolveConfidence(result);
                    const confidenceColor = getConfidenceColor(matchConfidence);
                    const confidenceLabel = getConfidenceLabel(matchConfidence);
                    const aiResearched = isAiResearched(result);

                    return (
                      <tr key={index} className="hover:bg-gray-50 border-b border-gray-100">
                        {/* IMAGE */}
                        <td className="px-4 py-3 text-sm text-center">
                          {renderLineImage(result, index)}
                        </td>

                        {/* QTY */}
                        <td className="px-4 py-3 text-sm text-center">
                          {result.original_claim.qty}
                        </td>

                        {/* USER REQUEST */}
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-gray-900">{result.original_claim.description}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {result.original_claim.maker} {result.original_claim.model}
                          </div>
                        </td>

                        {/* BEST MATCH */}
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-gray-900 flex items-center gap-2 flex-wrap" data-testid={`best-match-title-${index}`}>
                            {bestMatchTitle(result)}
                            {aiResearched && (
                              <span
                                data-testid={`ai-badge-${index}`}
                                className="inline-flex items-center gap-1 rounded-full border border-purple-300 bg-purple-50 text-purple-800 text-[10px] font-semibold px-2 py-0.5"
                                title="Price provided by AI research"
                              >
                                <Bot className="w-3 h-3" aria-hidden="true" />
                                AI-researched
                              </span>
                            )}
                          </div>
                          {bestMatchMakerModel(result) && (
                            <div className="text-xs text-gray-500 mt-1" data-testid={`best-match-maker-model-${index}`}>
                              {bestMatchMakerModel(result)}
                            </div>
                          )}
                          {aiResearched && result.match_details && (
                            <div className="text-[11px] italic text-purple-700 mt-1">
                              {result.match_details}
                            </div>
                          )}
                          {result.source_url && (
                            <a
                              href={result.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`ai-source-link-${index}`}
                              className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold text-cyan-700 hover:text-cyan-900 underline"
                              title="Reference URL suggested by AI research"
                            >
                              <ExternalLink className="w-3 h-3" aria-hidden="true" />
                              View source
                            </a>
                          )}
                        </td>

                        {/* MATCH CONFIDENCE */}
                        <td className="px-4 py-3 text-sm text-center">
                          <div
                            className={`inline-flex items-center px-3 py-1 rounded-full border ${bestMatchTitle(result) ? confidenceColor : "text-gray-500 bg-gray-50 border-gray-200"}`}
                            data-testid={`match-confidence-${index}`}
                          >
                            {bestMatchTitle(result) ? (
                              <>
                                <span className="font-bold text-lg">{matchConfidence}%</span>
                                <span className="ml-2 text-xs font-medium">{confidenceLabel}</span>
                              </>
                            ) : (
                              <span className="font-bold text-lg">—</span>
                            )}
                          </div>
                        </td>

                        {/* STATUS */}
                        <td className="px-4 py-3 text-sm text-center">
                          {renderLineStatus(result, index)}
                        </td>

                        {/* MARKET PRICE */}
                        <td className="px-4 py-3 text-sm text-right">
                          {renderLinePrice(result, index)}
                        </td>
                        
                        {/* ACTIONS */}
                        <td className="px-4 py-3 text-sm text-center">
                          {renderLineActions(result, index)}
                        </td>
                      </tr>
                    );
                  })}
                  
                  {/* Total Row */}
                  <tr className="bg-gray-100 font-bold border-t-2 border-gray-200">
                    <td className="px-4 py-3 text-sm" colSpan="6">
                      <strong>TOTAL VALUATION</strong>
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-green-800 font-bold">
                      {formatCurrency(computeTotalExcludingRejected())}
                    </td>
                    <td className="px-4 py-3 text-sm"></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mobile: one stacked card per line */}
            <div className="md:hidden px-4 pb-4 space-y-4" data-testid="mobile-line-cards">
              {(assessmentResults?.results || []).map((result, index) => (
                renderMobileCard(result, index)
              ))}
            </div>
            <div
              className="md:hidden sticky bottom-0 z-10 flex items-center justify-between bg-white/95 backdrop-blur border-t-2 border-green-200 px-4 py-3 rounded-b-2xl shadow-[0_-4px_12px_rgba(0,0,0,0.06)]"
              data-testid="mobile-total-bar"
            >
              <span className="text-xs font-bold uppercase tracking-wider text-gray-700">Total Valuation</span>
              <span className="text-lg font-bold text-green-800" data-testid="mobile-total-value">
                {formatCurrency(computeTotalExcludingRejected())}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Print-Only View */}
      <div id="print-content" className="print-only">
        <style>{`
          @media print {
            body * {
              visibility: hidden !important;
            }
            #print-content, #print-content * {
              visibility: visible !important;
            }
            #print-content {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              padding: 20px !important;
              background: white !important;
            }
            @page {
              size: A4;
              margin: 1cm;
            }
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
          }
          @media screen {
            #print-content {
              display: none;
            }
          }
        `}</style>

        {/* Print Header */}
        <div style={{ marginBottom: '30px', borderBottom: '3px solid #14b8a6', paddingBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_dc1c0b21-5011-47e1-a19d-11de6678deed/artifacts/55cbyjpt_Logo.png" 
              alt="PriceValuator Logo"
              style={{ height: '80px', objectFit: 'contain' }}
            />
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#0f766e', fontFamily: 'Montserrat, sans-serif' }}>Case {caseData?.case_number}</div>
              {caseData?.blexbot_request_number && (
                <div style={{ marginTop: '4px', fontSize: '12px', fontWeight: 600, color: '#0E7490' }}>
                  ID # {caseData.blexbot_request_number}
                </div>
              )}
              <div style={{ fontSize: '14px', color: '#6B7280', marginTop: '4px', fontFamily: 'Montserrat, sans-serif' }}>User ID: {currentUser?.username || 'N/A'}</div>
            </div>
          </div>
        </div>

        {/* Print Indicators */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
          <div style={{ padding: '20px', backgroundColor: '#D1FAE5', borderRadius: '12px', border: '2px solid #10B981' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#065F46', marginBottom: '8px' }}>Total Valuation</div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#047857' }}>
              {formatCurrency(computeTotalExcludingRejected())}
            </div>
          </div>
          <div style={{ padding: '20px', backgroundColor: '#E9D5FF', borderRadius: '12px', border: '2px solid #A855F7' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#6B21A8', marginBottom: '8px' }}>Total Products Valuated</div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#7C3AED' }}>
              {assessmentResults?.results?.reduce((sum, r) => sum + r.original_claim.qty, 0) || 0}
            </div>
          </div>
        </div>

        {/* Print Table */}
        <div style={{ marginTop: '20px' }}>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#4F46E5', marginBottom: '15px' }}>Assessment Results</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #E5E7EB' }}>
            <thead>
              <tr style={{ backgroundColor: '#F3F4F6' }}>
                <th style={{ padding: '12px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#374151', border: '1px solid #E5E7EB' }}></th>
                <th style={{ padding: '12px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#374151', border: '1px solid #E5E7EB' }}>QTY</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#374151', border: '1px solid #E5E7EB' }}>BEST MATCH</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '11px', fontWeight: '600', color: '#374151', border: '1px solid #E5E7EB' }}>PRICE</th>
              </tr>
            </thead>
            <tbody>
              {assessmentResults?.results?.map((result, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #E5E7EB' }}>
                  {/* Image */}
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #E5E7EB' }}>
                    {result.matched_product?.image_base64 ? (
                      <img 
                        src={result.matched_product.image_base64} 
                        alt={result.matched_product.description}
                        style={{ width: '50px', height: '50px', objectFit: 'cover', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                      />
                    ) : (
                      <div style={{ width: '50px', height: '50px', border: '1px dashed #D1D5DB', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', color: '#9CA3AF' }}>
                        No image
                      </div>
                    )}
                  </td>
                  
                  {/* QTY */}
                  <td style={{ padding: '8px', textAlign: 'center', fontSize: '13px', border: '1px solid #E5E7EB' }}>
                    {result.original_claim.qty}
                  </td>
                  
                  {/* BEST MATCH */}
                  <td style={{ padding: '8px', fontSize: '13px', border: '1px solid #E5E7EB' }}>
                    <div style={{ fontWeight: '500', color: '#111827' }}>
                      {bestMatchTitle(result)}
                    </div>
                    {bestMatchMakerModel(result) && (
                      <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>
                        {bestMatchMakerModel(result)}
                      </div>
                    )}
                  </td>
                  
                  {/* MARKET PRICE */}
                  <td style={{ padding: '8px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: isPriceEdited(index) ? '#2563EB' : '#059669', border: '1px solid #E5E7EB' }}>
                    {formatCurrency(getPrice(index, result.market_price))}
                  </td>
                </tr>
              ))}
              
              {/* Total Row */}
              <tr style={{ backgroundColor: '#F3F4F6', fontWeight: 'bold' }}>
                <td colSpan="3" style={{ padding: '12px', fontSize: '14px', border: '1px solid #E5E7EB' }}>
                  <strong>TOTAL VALUATION</strong>
                </td>
                <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: 'bold', color: '#047857', border: '1px solid #E5E7EB' }}>
                  {formatCurrency(computeTotalExcludingRejected())}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AssessmentPreview;